import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { AudioLines, Loader2 } from "lucide-react";

export default function Auth() {
    const [mode, setMode] = useState("login");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);
    const { setToken, user } = useAuth();
    const nav = useNavigate();

    // Handle Emergent Google session_id from URL hash
    useEffect(() => {
        const hash = window.location.hash;
        if (hash.includes("session_id=")) {
            const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
            if (sid) {
                (async () => {
                    setBusy(true);
                    try {
                        const res = await api.post("/auth/google", { session_id: sid });
                        setToken(res.data.token, res.data.user);
                        toast.success("Welcome!");
                        window.location.hash = "";
                        nav("/app");
                    } catch (e) {
                        toast.error("Google sign-in failed", { description: e?.response?.data?.detail || e.message });
                    } finally {
                        setBusy(false);
                    }
                })();
            }
        }
    }, [setToken, nav]);

    useEffect(() => {
        if (user) nav("/app");
    }, [user, nav]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const path = mode === "login" ? "/auth/login" : "/auth/signup";
            const body = mode === "login" ? { email, password } : { email, password, name };
            const res = await api.post(path, body);
            setToken(res.data.token, res.data.user);
            toast.success(mode === "login" ? "Welcome back" : "Account created");
            nav("/app");
        } catch (err) {
            toast.error("Authentication failed", { description: err?.response?.data?.detail || err.message });
        } finally {
            setBusy(false);
        }
    };

    const googleSignIn = () => {
        const redirect = window.location.origin + "/auth";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
    };

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <div className="flex-1 grid lg:grid-cols-2">
                {/* Left side */}
                <div className="flex items-center justify-center p-6 sm:p-12">
                    <div className="w-full max-w-sm fade-in-up">
                        <Link to="/" className="inline-flex items-center gap-2 mb-10" data-testid="auth-logo">
                            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
                                <AudioLines className="w-4 h-4 text-white" strokeWidth={2.5} />
                            </div>
                            <span className="text-lg font-semibold tracking-tight text-slate-900">Transcribely</span>
                        </Link>

                        <h1 className="text-3xl font-semibold tracking-tight text-slate-900" data-testid="auth-title">
                            {mode === "login" ? "Welcome back" : "Create your account"}
                        </h1>
                        <p className="mt-2 text-sm text-slate-500">
                            {mode === "login" ? "Sign in to continue transcribing." : "Free forever — 20 minutes a month."}
                        </p>

                        <button
                            onClick={googleSignIn}
                            disabled={busy}
                            className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
                            data-testid="google-signin-btn"
                        >
                            <GoogleIcon /> Continue with Google
                        </button>

                        <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
                            <div className="flex-1 h-px bg-slate-200" />
                            <span>or</span>
                            <div className="flex-1 h-px bg-slate-200" />
                        </div>

                        <form onSubmit={submit} className="space-y-3">
                            {mode === "signup" && (
                                <div>
                                    <label className="text-xs font-medium text-slate-500">Name</label>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all"
                                        placeholder="Your name"
                                        data-testid="signup-name"
                                    />
                                </div>
                            )}
                            <div>
                                <label className="text-xs font-medium text-slate-500">Email</label>
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all"
                                    placeholder="you@example.com"
                                    data-testid="auth-email"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-slate-500">Password</label>
                                <input
                                    type="password"
                                    required
                                    minLength={6}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all"
                                    placeholder="At least 6 characters"
                                    data-testid="auth-password"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={busy}
                                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 transition-colors disabled:opacity-50"
                                data-testid="auth-submit"
                            >
                                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                                {mode === "login" ? "Sign in" : "Create account"}
                            </button>
                        </form>

                        <p className="mt-6 text-sm text-slate-500 text-center">
                            {mode === "login" ? "New here?" : "Have an account?"}{" "}
                            <button
                                onClick={() => setMode(mode === "login" ? "signup" : "login")}
                                className="font-medium text-indigo-600 hover:text-indigo-700"
                                data-testid="toggle-auth-mode"
                            >
                                {mode === "login" ? "Create one" : "Sign in"}
                            </button>
                        </p>
                    </div>
                </div>

                {/* Right side decorative */}
                <div className="hidden lg:block relative bg-slate-50 overflow-hidden">
                    <div className="absolute inset-0 bg-grid opacity-50" />
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/50 via-transparent to-transparent" />
                    <div className="relative h-full flex items-center justify-center p-12">
                        <div className="max-w-md space-y-6">
                            <FakeTranscript />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function GoogleIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 18 18">
            <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.21 1.13-.84 2.08-1.79 2.72v2.26h2.9c1.7-1.56 2.69-3.86 2.69-6.62z" />
            <path fill="#34A853" d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.9-2.26c-.81.54-1.84.86-3.06.86-2.34 0-4.32-1.58-5.03-3.7H.95v2.33A9 9 0 0 0 9 18z" />
            <path fill="#FBBC05" d="M3.97 10.71c-.18-.54-.28-1.12-.28-1.71s.1-1.17.28-1.71V4.96H.95A9 9 0 0 0 0 9c0 1.45.35 2.83.95 4.04l3.02-2.33z" />
            <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .95 4.96l3.02 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
        </svg>
    );
}

function FakeTranscript() {
    const items = [
        { speaker: "Speaker 1", time: "00:02", text: "So the way I think about it — transcription should just be invisible. You shouldn't have to think about it." },
        { speaker: "Speaker 2", time: "00:11", text: "Right, and the language detection makes a huge difference. We don't even ask the user." },
        { speaker: "Speaker 1", time: "00:18", text: "Exactly. Drop a file, get a clean transcript with timestamps. That's it." },
    ];
    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2 text-xs text-slate-400 font-mono">
                <span className="w-2 h-2 rounded-full bg-emerald-500" /> Live preview · auto-detected: English
            </div>
            {items.map((it, i) => (
                <div key={i} className="rounded-xl bg-white border border-slate-200 p-4 shadow-sm">
                    <div className="flex items-baseline gap-3 text-xs">
                        <span className="font-semibold text-indigo-600">{it.speaker}</span>
                        <span className="font-mono text-slate-400">{it.time}</span>
                    </div>
                    <p className="mt-1.5 text-sm text-slate-700 leading-relaxed">{it.text}</p>
                </div>
            ))}
        </div>
    );
}
